/**
 * HustleSuite Support page — /support
 *
 * Brand colours: terracotta (#c55a48) primary, amber (#f5c13d) accent,
 * cream (#faf7ef) canvas. Header + footer wrap the contact form.
 * Submissions POST to /api/support/contact — routed to the AI Support
 * Worker for triage and auto-response.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const styles = {
  page: {
    minHeight: "100vh",
    background: "#faf7ef",
    fontFamily: "-apple-system, Segoe UI, Roboto, Arial, sans-serif",
    color: "#0f172a",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    background: "#c55a48",
    color: "#faf7ef",
    padding: "20px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontWeight: 700,
    fontSize: 22,
    color: "#faf7ef",
    textDecoration: "none",
  },
  hMark: {
    width: 36, height: 36, borderRadius: 10,
    background: "#faf7ef", color: "#c55a48",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 22, fontWeight: 800,
  },
  navLink: { color: "#faf7ef", textDecoration: "none", fontSize: 14,
              opacity: 0.9, marginLeft: 20 },
  hero: {
    padding: "56px 24px 32px",
    maxWidth: 780,
    margin: "0 auto",
    textAlign: "center",
  },
  h1: { fontSize: 40, margin: "0 0 12px", fontWeight: 700 },
  lead: { fontSize: 17, color: "#4b5563", margin: 0, lineHeight: 1.5 },
  card: {
    maxWidth: 620,
    margin: "24px auto 80px",
    background: "#fff",
    border: "1px solid #ece7d8",
    borderRadius: 16,
    padding: 32,
    boxShadow: "0 6px 24px rgba(0,0,0,0.04)",
  },
  row: { display: "flex", flexDirection: "column", marginBottom: 18 },
  label: { fontSize: 13, fontWeight: 600, marginBottom: 6,
            color: "#4b5563", letterSpacing: "0.02em" },
  input: {
    padding: "12px 14px", fontSize: 16, borderRadius: 10,
    border: "1px solid #d6d3d1", background: "#fff",
    outline: "none",
  },
  textarea: {
    padding: "12px 14px", fontSize: 16, borderRadius: 10,
    border: "1px solid #d6d3d1", background: "#fff", outline: "none",
    resize: "vertical", minHeight: 140, fontFamily: "inherit",
  },
  btn: {
    padding: "14px 28px", background: "#c55a48", color: "#faf7ef",
    border: "none", borderRadius: 10, fontSize: 16, fontWeight: 600,
    cursor: "pointer", width: "100%", marginTop: 8,
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  success: {
    background: "#f0fdf4", border: "1px solid #bbf7d0",
    borderRadius: 12, padding: 20, color: "#15803d", textAlign: "center",
  },
  error: {
    background: "#fef2f2", border: "1px solid #fecaca",
    borderRadius: 12, padding: 16, color: "#b91c1c", marginBottom: 16,
  },
  footer: {
    marginTop: "auto",
    padding: "24px",
    background: "#0f172a",
    color: "#faf7ef",
    textAlign: "center",
    fontSize: 13,
  },
  footerLink: { color: "#f5c13d", textDecoration: "none", margin: "0 10px" },
};


export default function SupportPage() {
  const [form, setForm] = useState({
    name: "", email: "", subject: "", module: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ticket, setTicket] = useState(null);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.subject || !form.message) {
      setError("Please fill in every required field.");
      return;
    }
    if (form.message.length < 10) {
      setError("Please give us at least 10 characters so we can help.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/support/contact`, form);
      setTicket(r.data);
    } catch (err) {
      setError(
        err.response?.data?.detail?.[0]?.msg ||
        err.response?.data?.detail ||
        "Something went wrong. Please email support@hustlesuite.au directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.page} data-testid="support-page">
      <header style={styles.header}>
        <Link to="/" style={styles.logo} data-testid="support-header-logo">
          <span style={styles.hMark}>H</span>
          <span>HustleSuite</span>
        </Link>
        <nav>
          <Link to="/" style={styles.navLink} data-testid="support-nav-home">Home</Link>
          <Link to="/legal/privacy" style={styles.navLink} data-testid="support-nav-privacy">Privacy</Link>
          <Link to="/legal/terms" style={styles.navLink} data-testid="support-nav-terms">Terms</Link>
        </nav>
      </header>

      <section style={styles.hero}>
        <h1 style={styles.h1}>How can we help?</h1>
        <p style={styles.lead}>
          Message our AI Support Worker below — we usually respond within a
          few minutes, and always within 24 hours. You'll get an email with
          your ticket reference right after you submit.
        </p>
      </section>

      <div style={styles.card}>
        {ticket ? (
          <div style={styles.success} data-testid="support-success">
            <div style={{ fontSize: 40, marginBottom: 10 }}>✓</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
              Message received
            </div>
            <div style={{ fontSize: 14, marginBottom: 12 }}>
              Ticket reference:{" "}
              <code style={{ background: "#dcfce7", padding: "2px 8px",
                              borderRadius: 6 }}>
                {ticket.ticket_id}
              </code>
            </div>
            <div style={{ fontSize: 14, color: "#166534" }}>
              {ticket.message}
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            {error && (
              <div style={styles.error} data-testid="support-error">{error}</div>
            )}
            <div style={styles.row}>
              <label style={styles.label} htmlFor="name">Your name *</label>
              <input id="name" style={styles.input} value={form.name}
                     onChange={update("name")} required maxLength={120}
                     data-testid="support-name" />
            </div>
            <div style={styles.row}>
              <label style={styles.label} htmlFor="email">Email *</label>
              <input id="email" type="email" style={styles.input}
                     value={form.email} onChange={update("email")}
                     required data-testid="support-email" />
            </div>
            <div style={styles.row}>
              <label style={styles.label} htmlFor="module">Which module?</label>
              <select id="module" style={styles.input} value={form.module}
                       onChange={update("module")} data-testid="support-module">
                <option value="">General / Not sure</option>
                <option value="plaza">HustlePlaza (Shopping)</option>
                <option value="tackle">HustleTackle (Fishing)</option>
                <option value="property">HustleProperty (Real Estate)</option>
                <option value="eats">HustleEats (Booking / Food)</option>
                <option value="hydro">HustleHydro (Flood Reports)</option>
                <option value="wallet">Wallet / Loyalty passes</option>
                <option value="account">Account / Login / Billing</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div style={styles.row}>
              <label style={styles.label} htmlFor="subject">Subject *</label>
              <input id="subject" style={styles.input} value={form.subject}
                     onChange={update("subject")} required maxLength={200}
                     data-testid="support-subject" />
            </div>
            <div style={styles.row}>
              <label style={styles.label} htmlFor="message">
                How can we help? *
              </label>
              <textarea id="message" style={styles.textarea}
                         value={form.message} onChange={update("message")}
                         required minLength={10} maxLength={5000}
                         data-testid="support-message"
                         placeholder="Tell us what's happening. The more detail, the faster we solve it." />
            </div>
            <button type="submit" style={{
              ...styles.btn,
              ...(submitting ? styles.btnDisabled : {}),
            }} disabled={submitting} data-testid="support-submit">
              {submitting ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>

      <footer style={styles.footer}>
        <div style={{ marginBottom: 8 }}>
          © 2026 HustleSuite · Operated by LOCAL HUSTLE, Queensland Australia
        </div>
        <div>
          <Link to="/legal/privacy" style={styles.footerLink}>Privacy</Link>
          <Link to="/legal/terms" style={styles.footerLink}>Terms</Link>
          <a href="mailto:support@hustlesuite.au" style={styles.footerLink}>
            support@hustlesuite.au
          </a>
        </div>
      </footer>
    </div>
  );
}
